import React, { useEffect, useRef, useState } from "react";
import { Sun, Moon, Leaf, Monitor, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";

/**
 * Iter59.p24 — Compact theme picker for the header. Shows the icon of
 * the currently-active mode; opens a dropdown of the 4 modes on click.
 * Deeper customization (colour pickers, advanced tokens) lives on the
 * `/settings/theme` page — this menu links to it.
 */
const MODES = [
    { key: "light",   label: "Light",         Icon: Sun },
    { key: "dark",    label: "Dark",          Icon: Moon },
    { key: "comfort", label: "Eye Comfort",   Icon: Leaf },
    { key: "system",  label: "System Default", Icon: Monitor },
];

export default function ThemeToggleButton() {
    const { mode, setMode } = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    const current = MODES.find((m) => m.key === mode) || MODES[3];
    const CurrentIcon = current.Icon;

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div className="relative" ref={menuRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-[color:var(--menu-hover-bg)] transition"
                style={{ color: "var(--text-secondary)" }}
                aria-label={`Theme: ${current.label}`}
                title={`Theme: ${current.label}`}
                data-testid="theme-toggle-btn"
            >
                <CurrentIcon className="h-4 w-4" />
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid="theme-toggle-menu"
                    className="absolute right-0 mt-2 rounded-lg shadow-lg z-50 py-1"
                    style={{
                        minWidth: 220,
                        background: "var(--surface-card)",
                        border: "1px solid var(--border-default)",
                        boxShadow: "var(--shadow-md)",
                    }}
                >
                    {MODES.map((m) => {
                        const Icon = m.Icon;
                        const active = m.key === mode;
                        return (
                            <button
                                key={m.key}
                                type="button"
                                role="menuitem"
                                onClick={() => { setMode(m.key); setOpen(false); }}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition"
                                style={{
                                    color: "var(--text-primary)",
                                    background: active ? "var(--menu-selected-bg)" : "transparent",
                                }}
                                data-testid={`theme-mode-${m.key}`}
                                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--menu-hover-bg)"; }}
                                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                            >
                                <Icon className="h-4 w-4" style={{ color: active ? "var(--menu-selected-text)" : "var(--text-secondary)" }} />
                                <span className="flex-1">{m.label}</span>
                                {active && <Check className="h-3.5 w-3.5" style={{ color: "var(--menu-selected-text)" }} />}
                            </button>
                        );
                    })}
                    <div style={{ borderTop: "1px solid var(--border-default)", margin: "4px 0" }} />
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setOpen(false); navigate("/settings/theme"); }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition"
                        style={{ color: "var(--link-color)" }}
                        data-testid="theme-open-settings"
                        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--menu-hover-bg)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                        Customize…
                    </button>
                </div>
            )}
        </div>
    );
}
